import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OfficeMap from "@/components/ikra/OfficeMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplyForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cv, setCv] = useState(null);
  const fileRef = useRef(null);

  const handle = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("CV jest za duże", { description: "Maksymalny rozmiar to 5 MB." });
      e.target.value = "";
      return;
    }
    setCv(f);
  };

  const clearCv = () => {
    setCv(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      data.append("full_name", form.full_name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("city", form.city);
      data.append("consent", "true");
      if (cv) data.append("cv", cv);

      await axios.post(`${API}/applications`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Dzięki, odezwiemy się.");
      setForm({ full_name: "", email: "", phone: "", city: "" });
      clearCv();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Spróbuj ponownie za chwilę.";
      toast.error("Nie udało się wysłać", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="aplikuj"
      data-testid="apply-section"
      className="relative py-24 lg:py-36 bg-[var(--ikra-bg-alt)] border-t border-[var(--ikra-line)] text-[var(--ikra-ink)]"
    >
      <div className="max-w-[1100px] mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--ikra-muted)]">
              Formularz · Pierwszy krok
            </div>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.04] tracking-tight">
              Pogadajmy u nas{" "}
              <span className="text-[var(--ikra-muted)]">
                w biurze i sprawdźmy, czy to miejsce dla Ciebie.
              </span>
            </h2>
            <p className="mt-7 text-lg text-[var(--ikra-muted)] leading-relaxed max-w-xl">
              Zaproszenie na spotkanie u nas. Bez sztywnego scenariusza.
              Sprawdzamy dopasowanie z obu stron.
            </p>

            <ul className="mt-9 space-y-3 text-[var(--ikra-muted)]">
              {[
                "Spotkanie u nas w biurze",
                "Bez wymogu doświadczenia",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--ikra-gold-bright)]" />
                  {t}
                </li>
              ))}
            </ul>

            <OfficeMap />
          </div>

          <div className="lg:col-span-6">
            {submitted ? (
              <Success onReset={() => setSubmitted(false)} />
            ) : (
              <form
                onSubmit={submit}
                data-testid="apply-form"
                className="p-7 lg:p-9 rounded-2xl bg-[var(--ikra-bg-deep)] text-[var(--ikra-ink)] border border-[var(--ikra-line)] shadow-2xl shadow-black/20"
              >
                <div className="space-y-5">
                  <Field label="Imię i nazwisko">
                    <Input
                      data-testid="apply-input-name"
                      value={form.full_name}
                      onChange={handle("full_name")}
                      placeholder="np. Anna Kowalska"
                      required
                      minLength={2}
                      className="h-12 bg-[var(--ikra-bg)] border-[var(--ikra-line)] text-[var(--ikra-ink)] placeholder:text-[var(--ikra-muted-soft)] focus-visible:ring-[var(--ikra-ink)]"
                    />
                  </Field>
                  <Field label="Telefon">
                    <Input
                      data-testid="apply-input-phone"
                      value={form.phone}
                      onChange={handle("phone")}
                      placeholder="+48 600 000 000"
                      required
                      className="h-12 bg-[var(--ikra-bg)] border-[var(--ikra-line)] text-[var(--ikra-ink)] placeholder:text-[var(--ikra-muted-soft)] focus-visible:ring-[var(--ikra-ink)]"
                    />
                  </Field>
                  <Field label="E-mail">
                    <Input
                      data-testid="apply-input-email"
                      type="email"
                      value={form.email}
                      onChange={handle("email")}
                      placeholder="np. anna@email.pl"
                      required
                      className="h-12 bg-[var(--ikra-bg)] border-[var(--ikra-line)] text-[var(--ikra-ink)] placeholder:text-[var(--ikra-muted-soft)] focus-visible:ring-[var(--ikra-ink)]"
                    />
                  </Field>
                  <Field label="Miasto">
                    <Input
                      data-testid="apply-input-city"
                      value={form.city}
                      onChange={handle("city")}
                      placeholder="np. Wrocław"
                      required
                      className="h-12 bg-[var(--ikra-bg)] border-[var(--ikra-line)] text-[var(--ikra-ink)] placeholder:text-[var(--ikra-muted-soft)] focus-visible:ring-[var(--ikra-ink)]"
                    />
                  </Field>
                  <Field label="CV (opcjonalnie)">
                    <input
                      ref={fileRef}
                      id="apply-cv"
                      data-testid="apply-input-cv"
                      type="file"
                      accept=".pdf,.doc,.docx,.odt,.rtf,.txt"
                      onChange={onFile}
                      className="sr-only"
                    />
                    {cv ? (
                      <div
                        data-testid="apply-cv-selected"
                        className="h-12 px-4 flex items-center justify-between gap-3 rounded-md bg-[var(--ikra-bg)] border border-[var(--ikra-gold)]/60 text-sm"
                      >
                        <span className="flex items-center gap-2 truncate text-[var(--ikra-ink)]">
                          <Paperclip size={14} className="text-[var(--ikra-gold)] shrink-0" />
                          <span className="truncate">{cv.name}</span>
                          <span className="text-[var(--ikra-muted)] shrink-0">
                            {(cv.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={clearCv}
                          data-testid="apply-cv-remove"
                          aria-label="Usuń plik"
                          className="text-[var(--ikra-muted)] hover:text-[var(--ikra-ink)] transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="apply-cv"
                        data-testid="apply-cv-label"
                        className="h-12 px-4 flex items-center gap-3 rounded-md bg-[var(--ikra-bg)] border border-dashed border-[var(--ikra-line)] hover:border-[var(--ikra-gold)] text-sm text-[var(--ikra-muted)] cursor-pointer transition-colors"
                      >
                        <Paperclip size={14} className="text-[var(--ikra-gold)]" />
                        Załącz CV · PDF lub DOCX, max 5 MB
                      </label>
                    )}
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="apply-submit"
                  className="mt-8 w-full btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Wysyłanie…
                    </>
                  ) : (
                    <>
                      Porozmawiajmy <span aria-hidden>→</span>
                    </>
                  )}
                </button>

                <p className="mt-5 text-xs text-[var(--ikra-muted)] leading-relaxed">
                  Wysyłając formularz wyrażasz zgodę na przetwarzanie danych w
                  celu rekrutacji w IKRA Marketing & Sales.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-[var(--ikra-muted)] font-medium">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Success({ onReset }) {
  return (
    <div
      data-testid="apply-success"
      className="p-10 rounded-2xl bg-[var(--ikra-bg-deep)] text-[var(--ikra-ink)] border border-[var(--ikra-line)] shadow-2xl shadow-black/20 text-center"
    >
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ikra-gold)] text-black mx-auto">
        <CheckCircle2 size={26} />
      </div>
      <h3 className="mt-6 font-display text-3xl tracking-tight">
        Dziękujemy. Mamy Twoje zgłoszenie.
      </h3>
      <p className="mt-4 text-[var(--ikra-muted)] max-w-md mx-auto">
        Odezwiemy się do Ciebie telefonem lub mailem.
      </p>
      <button
        type="button"
        onClick={onReset}
        data-testid="apply-success-reset"
        className="mt-8 inline-flex items-center gap-2 text-sm text-[var(--ikra-ink)] ikra-link"
      >
        Wyślij kolejne zgłoszenie →
      </button>
    </div>
  );
}
